import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCarTechProfile, useUpdateCarReport, useReport } from '../hooks/useCarReports';
import { CreateReportDto, ReportsListResponse, ReportComment, carReportsApi } from '../api/carReportsApi';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useToast } from '@/components/ui/toast/Toast';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Button from '@/components/ui/button/Button';
import TextArea from '@/components/form/input/TextArea';
import InteractiveCarImage from '@/components/car/InteractiveCarImage';

interface EditCarReportFormProps {
  report: ReportsListResponse['data'][0];
  onSubmit: () => void;
  onClose: () => void;
  currentData?: Partial<CreateReportDto>;
  onDataChange?: (data: Partial<CreateReportDto>) => void;
  initialData?: Partial<CreateReportDto>;
}

interface DetailItem {
  id: string;
  detail: string;
  comments: ReportComment[];
}

interface FormErrors {
  mileage?: string;
  fuel_level?: string;
  charge_level?: string;
  details?: string;
  detailFields?: { [key: string]: string | undefined };
}

const EditCarReportForm: React.FC<EditCarReportFormProps> = ({
  report,
  onSubmit,
  onClose,
  currentData,
  onDataChange,
  initialData
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: techProfile, isLoading: isLoadingProfile } = useCarTechProfile(report.car.id);
  const { data: reportDetails, isLoading: isLoadingReport } = useReport(report.id);
  const updateReport = useUpdateCarReport();
  const { user } = useAuth();

  const defaultInitialData = useMemo(() => ({
    renting: report.renting?.id,
    activity: report.activity,
    mileage: report.mileage,
    fuel_level: report.fuel_level,
    charge_level: report.charge_level,
    details: reportDetails?.details || []
  }), [report, reportDetails]);

  // Инициализируем formData функционально, чтобы предотвратить сброс при ре-рендерах
  const [formData, setFormData] = useState<Partial<CreateReportDto>>(() => {
    // Инициализируем только базовыми значениями из report
    return {
      renting: report.renting?.id,
      activity: report.activity,
      mileage: report.mileage || 0,
      fuel_level: report.fuel_level || 0,
      charge_level: report.charge_level || 0,
    };
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [details, setDetails] = useState<DetailItem[]>([]);
  const isInitialized = useRef(false);

  // Инициализируем formData только один раз при монтировании
  useEffect(() => {
    if (!isInitialized.current) {
      // Первая инициализация - используем initialData или defaultInitialData только один раз
      // Ждем, пока загрузятся reportDetails, если они нужны для defaultInitialData
      if (reportDetails || !initialData) {
        const dataToUse = initialData || defaultInitialData;
        if (dataToUse) {
          setFormData(prev => ({
            ...prev,
            ...dataToUse,
            // Не перезаписываем mileage, fuel_level, charge_level если они уже были инициализированы
            mileage: dataToUse.mileage ?? prev.mileage ?? report.mileage ?? 0,
            fuel_level: dataToUse.fuel_level ?? prev.fuel_level ?? report.fuel_level ?? 0,
            charge_level: dataToUse.charge_level ?? prev.charge_level ?? report.charge_level ?? 0,
          }));
        }
        isInitialized.current = true;
      }
    }
    // После инициализации НЕ обновляем formData из initialData, чтобы не сбрасывать введенные пользователем данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetails]); // Зависим от reportDetails, чтобы инициализировать когда данные загрузятся

  // Initialize details from report data (только один раз при загрузке данных)
  useEffect(() => {
    if (reportDetails?.details && details.length === 0) {
      const initialDetails = reportDetails.details.map(detail => ({
        id: crypto.randomUUID(),
        detail: detail.detail,
        comments: detail.comments,
      }));
      setDetails(initialDetails);
    }
  }, [reportDetails?.details, details.length]);

  // Notify parent about data changes (только после инициализации)
  useEffect(() => {
    if (onDataChange && isInitialized.current) {
      onDataChange({
        ...formData,
        details: details.map(d => ({
          detail: d.detail,
          comments: d.comments
        }))
      });
    }
  }, [formData, details, onDataChange]);


  // Получаем список доступных деталей (исключая уже выбранные)
  const availableDetails = useMemo(() => {
    if (!techProfile) return [];
    const selectedDetails = new Set(details.map(d => d.detail));
    return techProfile.details.filter(d => !selectedDetails.has(d.detail));
  }, [techProfile, details]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.mileage || formData.mileage <= 0) {
      newErrors.mileage = t('cars.report.errors.mileage_positive');
    }

    if (formData.fuel_level !== undefined && formData.fuel_level > 100) {
      newErrors.fuel_level = t('cars.report.errors.fuel_level_max');
    }

    // Validate details
    const detailFieldErrors: { [key: string]: string } = {};
    let hasEmptyDetails = false;

    details.forEach(detail => {
      if (!detail.detail) {
        detailFieldErrors[detail.id] = t('cars.report.errors.detail_required');
        hasEmptyDetails = true;
      }
    });

    if (hasEmptyDetails) {
      newErrors.detailFields = detailFieldErrors;
      newErrors.details = t('cars.report.errors.select_all_details');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddDetail = (detailId: string) => {
    // Проверяем, не добавлена ли уже эта деталь
    if (details.some(d => d.detail === detailId)) {
      return;
    }

    const newId = crypto.randomUUID();
    setDetails(prev => [{
      id: newId,
      detail: detailId,
      comments: [{
        text: '',
        file_name: '',
        original_file_name: '',
        employee: user?.user_id || '',
        created_at: new Date().toISOString(),
      }]
    }, ...prev]);
  };

  const handleRemoveDetail = (detailId: string) => {
    setDetails(prev => prev.filter(d => d.detail !== detailId));
  };

  const handleDetailChange = (detailId: string, field: 'comment', value: string) => {
    setDetails(prev => prev.map(d => {
      if (d.detail !== detailId) return d;

      // Для комментария обновляем текст последнего текстового комментария
      // или создаем новый, если нет текстовых комментариев
      const textComment = d.comments.find(c => !c.file_url);
      if (textComment) {
        return {
          ...d,
          comments: d.comments.map(c =>
            c === textComment ? { ...c, text: value } : c
          )
        };
      } else {
        return {
          ...d,
          comments: [
            ...d.comments,
            {
              text: value,
              file_name: '',
              original_file_name: '',
              employee: user?.user_id || '',
              created_at: new Date().toISOString(),
            }
          ]
        };
      }
    }));
  };

  const handleImageUpload = async (detailId: string, file: File) => {
    try {
      const detail = details.find(d => d.detail === detailId);
      if (!detail || !detail.detail) return;

      const newComment = await carReportsApi.uploadReportImage(report.id, detail.detail, file);

      setDetails(prev => prev.map(d =>
        d.detail === detailId
          ? { ...d, comments: [...d.comments, newComment] }
          : d
      ));
    } catch (error) {
      console.error('Failed to upload image:', error);
    }
  };

  const handleRemoveImage = (detailId: string, commentId: string) => {
    setDetails(prev => prev.map(d =>
      d.detail === detailId
        ? { ...d, comments: d.comments.filter(c => c.id !== commentId) }
        : d
    ));
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        console.error('Form validation failed:', errors);
        return;
      }

      if (!user?.user_id) {
        console.error('No employee ID available');
        return;
      }

      const reportData: Partial<CreateReportDto> = {
        mileage: formData.mileage || 0,
        fuel_level: formData.fuel_level || 0,
        charge_level: formData.charge_level || 0,
        employee: user.user_id,
        details: details
          .filter(d => d.detail && d.comments.some(c => c.text.trim() || c.file_url))
          .map(d => ({
            detail: d.detail,
            comments: d.comments
          })),
      };

      try {
        const response = await updateReport.mutateAsync({
          id: report.id,
          data: reportData,
        });
        onSubmit();
        onClose();
        toast.success(t('cars.report.edit_success'));
      } catch (mutationError) {
        console.error('Mutation error:', mutationError);
        throw mutationError;
      }
    } catch (error) {
      toast.error(t('cars.report.edit_error') + `<br/><br/>${error}`);
    }
  };

  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      mileage: value === '' ? 0 : parseInt(value)
    }));
    if (errors.mileage) {
      setErrors(prev => ({ ...prev, mileage: undefined }));
    }
  };

  const handleFuelLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      fuel_level: value === '' ? 0 : parseInt(value)
    }));
    if (errors.fuel_level) {
      setErrors(prev => ({ ...prev, fuel_level: undefined }));
    }
  };

  const handleChargeLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      charge_level: value === '' ? 0 : parseInt(value)
    }));
    if (errors.charge_level) {
      setErrors(prev => ({ ...prev, charge_level: undefined }));
    }
  };

  if (isLoadingProfile || isLoadingReport) {
    return <div>{t('Loading...')}</div>;
  }

  return (
    <>
      <div className="">
        <div className="grid grid-cols-1 gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>{t('cars.report.fields.report_type')}</Label>
              <select
                data-testid="report-form-field-activity-type"
                className="w-full rounded-lg border border-stroke bg-transparent px-4 py-2.5 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                value={formData.activity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    activity: e.target.value as 'delivery_to_client' | 'pickup_from_client',
                  }))
                }
                disabled
              >
                <option value="delivery_to_client">{t('cars.report.options.upon_delivering')}</option>
                <option value="pickup_from_client">{t('cars.report.options.upon_picking_up')}</option>
              </select>
            </div>

            <div>
              <Label>{t('cars.report.fields.mileage')}</Label>
              <Input
                testId="report-form-field-mileage"
                type="number"
                value={formData.mileage === 0 ? '' : formData.mileage}
                onChange={handleMileageChange}
                placeholder={t('cars.report.placeholders.enter_mileage')}
                error={errors.mileage}
              />
            </div>

            <div>
              <Label>{t('cars.report.fields.fuel_level')}</Label>
              <Input
                testId="report-form-field-fuel-level"
                type="number"
                min="0"
                max="100"
                value={formData.fuel_level === 0 ? '' : formData.fuel_level}
                onChange={handleFuelLevelChange}
                placeholder={t('cars.report.placeholders.enter_fuel_level')}
                error={errors.fuel_level}
              />
            </div>

            <div>
              <Label>{t('cars.report.fields.charge_level')}</Label>
              <Input
                testId="report-form-field-charge-level"
                type="number"
                min="0"
                max="100"
                value={formData.charge_level === 0 ? '' : formData.charge_level}
                onChange={handleChargeLevelChange}
                placeholder={t('cars.report.placeholders.enter_charge_level')}
                error={errors.charge_level}
              />
            </div>
          </div>

          <div>
            {/* Интерактивное изображение автомобиля */}
            {techProfile && (
              <div className="mb-6">
                <InteractiveCarImage
                  details={techProfile.details}
                  selectedDetails={details.map(d => d.detail).filter(Boolean)}
                  onDetailSelect={handleAddDetail}
                  onDetailDeselect={handleRemoveDetail}
                  availableDetails={availableDetails}
                  onDetailFocus={(detailId) => {
                    const detail = details.find(d => d.detail === detailId);
                    if (detail) {
                      const textarea = document.querySelector(`[data-testid="report-form-detail-comment-${detail.id}"]`) as HTMLTextAreaElement;
                      if (textarea) {
                        textarea.focus();
                        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Добавляем визуальное выделение
                        textarea.style.borderColor = '#3b82f6';
                        textarea.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                        setTimeout(() => {
                          textarea.style.borderColor = '';
                          textarea.style.boxShadow = '';
                        }, 2000);
                      }
                    }
                  }}
                />
              </div>
            )}

            {/* Список выбранных деталей с комментариями и изображениями - под изображением */}
            {details.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4 text-text-primary">
                  Selected Details ({details.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1200px]">
              {details.map((detail) => {
                const detailInfo = techProfile?.details.find(d => d.detail === detail.detail);
                return (
                  <div
                    key={detail.id}
                    className="border border-border-primary p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <Label>{detailInfo?.name || detail.detail}</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveDetail(detail.detail)}
                        className="!p-1.5 border-border-primary hover:border-danger hover:bg-danger/10"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-gray-500 group-hover:text-danger"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </Button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>{t('cars.report.fields.comment')}</Label>
                        <TextArea
                          testId={`report-form-detail-comment-${detail.id}`}
                          value={detail.comments.find(c => !c.file_url)?.text || ''}
                          onChange={(value) => handleDetailChange(detail.detail, 'comment', value)}
                          placeholder={t('cars.report.placeholders.add_comment')}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>{t('cars.report.fields.images')}</Label>
                        <div className="flex flex-wrap gap-2">
                          {detail.comments
                            .filter(c => c.file_url)
                            .map((comment, index) => (
                              <div key={index} className="relative group">
                                <a
                                  href={comment.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block w-20 h-20 rounded overflow-hidden"
                                >
                                  <img
                                    src={comment.file_url}
                                    alt={comment.original_file_name}
                                    className="w-full h-full object-cover"
                                  />
                                </a>
                                <button
                                  onClick={() => comment.id && handleRemoveImage(detail.detail, comment.id)}
                                  className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-gray-500"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-primary">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleImageUpload(detail.detail, file);
                                }
                              }}
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button
            testId="report-form-cancel-button"
            variant="outline"
            size="sm"
            onClick={onClose}
          >
            {t('common.buttons.cancel')}
          </Button>
          <Button
            testId="report-form-submit-button"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={updateReport.isPending}
          >
            {updateReport.isPending ? t('cars.report.buttons.saving') : t('cars.report.buttons.save_changes')}
          </Button>
        </div>
      </div>
    </>
  );
};

EditCarReportForm.displayName = 'EditCarReportForm';

export default EditCarReportForm;
